// LOGIC PORTED from LC2's AccountDashboard.js (commons/components/MenuDashboard/) +
// MenuDashboard.js's createHandleMenuClick ('portfolio' branch) — same permission-filtered
// account list (entities.accounts, populated by MainLayout's setAccounts effect) and same
// navigation (`navigate('account/:sfid')`). This was missing entirely before: the sidebar's
// avatar/name area was a static, non-interactive display.
//
// Unlike the header's Communities/Campaigns dropdowns (SearchableBreadcrumbDropdown.js),
// this filters the already-fully-loaded `entities.accounts` list client-side instead of
// hitting a server search endpoint — LC2's AccountDashboard.js didn't paginate/search
// accounts server-side either (a user's account list is expected to be small), and there's
// no confirmed server endpoint for it, so this stays simple and local.
//
// Not ported: LC2's "Add an account" option (isStakeAdmin-only, opens a NewAccount modal)
// — that modal/creation flow isn't built in the new UI yet.
import React, {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {Box, ButtonBase, InputBase, Popper, ClickAwayListener, Typography} from '@mui/material';

import {getAccounts} from '../../app/store/reducers/entities';
import {tokens} from '../../themes/ThemeCustomization';
import tableSearchIcon from '../../assets/shell/table-search-icon.svg';
import sidebarCollapseIcon from '../../assets/shell/sidebar-collapse-icon.svg';

const getInitials = (name) => {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
};

const AccountSwitcher = ({currentAccountName}) => {
    const navigate = useNavigate();
    const accounts = useSelector(getAccounts);

    const anchorRef = React.useRef(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
        if (!search.trim()) return accounts;
        const needle = search.trim().toLowerCase();
        return accounts.filter((account) => account.name?.toLowerCase().includes(needle));
    }, [accounts, search]);

    const handleOpen = () => {
        setSearch('');
        setOpen(true);
    };

    const handleSelect = (account) => {
        setOpen(false);
        navigate(`/account/${account.sfid}`);
    };

    return (
        <Box ref={anchorRef} sx={{flex: 1, minWidth: 0}}>
            <ButtonBase
                onClick={handleOpen}
                sx={{width: '100%', borderRadius: '6px', p: 0.5, gap: 1, justifyContent: 'flex-start'}}
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: '2px',
                        border: `1px solid ${tokens.color.neutral200}`,
                        backgroundColor: tokens.color.neutral950,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography variant="caption" fontWeight={600} sx={{color: tokens.color.white}}>
                        {getInitials(currentAccountName)}
                    </Typography>
                </Box>
                <Typography variant="body2" fontWeight={500} noWrap sx={{flex: 1, textAlign: 'left'}}>
                    {currentAccountName}
                </Typography>
                <Box component="img" src={sidebarCollapseIcon} alt="Switch account" sx={{width: 16, height: 16, flexShrink: 0, mr: 0.5}} />
            </ButtonBase>

            <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" sx={{zIndex: 1300}}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Box
                        sx={{
                            mt: 0.5,
                            width: '260px',
                            maxHeight: '320px',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: tokens.color.white,
                            border: `1px solid ${tokens.color.neutral200}`,
                            borderRadius: '8px',
                            boxShadow: tokens.shadow.toast,
                            overflow: 'hidden'
                        }}
                    >
                        {accounts.length > 5 && (
                            <Box sx={{p: 1, borderBottom: `1px solid ${tokens.color.neutral200}`}}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        border: `1px solid ${tokens.color.neutral200}`,
                                        borderRadius: '6px',
                                        height: 32,
                                        px: 1
                                    }}
                                >
                                    <Box component="img" src={tableSearchIcon} alt="" sx={{width: 14, height: 14}} />
                                    <InputBase
                                        autoFocus
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Search accounts"
                                        sx={{flex: 1, fontSize: '14px'}}
                                    />
                                </Box>
                            </Box>
                        )}
                        <Box sx={{overflowY: 'auto', flex: 1}}>
                            {filtered.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{p: 2, textAlign: 'center'}}>
                                    No accounts found.
                                </Typography>
                            )}
                            {filtered.map((account) => (
                                <ButtonBase
                                    key={account.sfid}
                                    onClick={() => handleSelect(account)}
                                    sx={{
                                        width: '100%',
                                        justifyContent: 'flex-start',
                                        px: 1.5,
                                        py: 1,
                                        '&:hover': {backgroundColor: tokens.color.neutral100}
                                    }}
                                >
                                    <Typography variant="body2" noWrap>
                                        {account.name}
                                    </Typography>
                                </ButtonBase>
                            ))}
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

export default AccountSwitcher;
